import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

const YAxisContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 50px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 20px;
  padding-bottom: 40px;
  z-index: 10;
  pointer-events: none;
`;

const YAxisLabel = styled.div`
  font-family: "Public Sans", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.5em;
  text-align: right;
  color: #535862;
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: auto;

  &::after {
    content: "";
    position: absolute;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    width: calc(100vw - 88px);
    max-width: 829px;
    height: 1px;
    background-color: #ffffff;
    opacity: 0.1;
    pointer-events: none;
  }
`;

// Y-axis value formatting is handled dynamically inside CustomYAxis based on range/precision

// Helper function to calculate Y-axis domain (exported for use in chart)
export const calculateYAxisDomain = (data) => {
  if (!data || data.length === 0) {
    return [0, 1000];
  }

  // Compute min/max across all numeric series keys except timestamp/name fields
  let min = Infinity;
  let max = -Infinity;

  data.forEach((item) => {
    Object.entries(item).forEach(([key, val]) => {
      if (key === "name" || key === "date") return; // skip x-axis fields
      if (typeof val === "number" && isFinite(val)) {
        if (val < min) min = val;
        if (val > max) max = val;
      }
    });
  });

  // If no valid values found, return sensible default
  if (min === Infinity || max === -Infinity) {
    return [0, 1000];
  }

  // Handle flat chart scenarios (min === max): add symmetric padding
  if (max === min) {
    const base = Math.abs(max) || 1;
    const pad = Math.max(1, base * 0.02); // at least 1 unit or 2% of value
    return [max - pad, max + pad];
  }

  // Normal range: add 10% padding, with at least 1 unit
  const range = max - min;
  const padding = Math.max(range * 0.1, 1);
  const lower = min - padding;
  const upper = max + padding;

  return [lower, upper];
};

export const CustomYAxis = ({ data, height = 240 }) => {
  const [min, max] = calculateYAxisDomain(data);
  const range = max - min;

  // Calculate Y-axis values without premature rounding to preserve precision
  const getYAxisValues = () => {
    const step = range / 5; // 6 labels = 5 intervals
    const values = [];
    for (let i = 5; i >= 0; i--) {
      values.push(min + step * i);
    }
    return values;
  };

  const yAxisValues = getYAxisValues();

  // Simpler compact formatting using K/M/B with limited decimals
  const formatYAxisValueSimple = (value) => {
    const absValue = Math.abs(value);

    // Billions: 1 decimal (e.g., 2.3B)
    if (absValue >= 1_000_000_000) {
      const billions = value / 1_000_000_000;
      const str = billions.toFixed(1).replace(/\.0$/, "");
      return `$${str}B`;
    }

    // Millions: up to 3 decimals to allow 1.623M when needed
    if (absValue >= 1_000_000) {
      const millions = value / 1_000_000;
      const str = millions
        .toFixed(2)
        .replace(/(\.\d*?[1-9])0+$/, "$1")
        .replace(/\.0+$/, "");
      return `$${str}M`;
    }

    // Thousands: integer rounding to keep labels clean (e.g., 100K)
    if (absValue >= 1_000) {
      const thousands = Math.round(value / 1_000);
      return `$${thousands}K`;
    }

    // Smaller values: nearest integer
    return `$${Math.round(value).toLocaleString()}`;
  };

  return (
    <YAxisContainer style={{ height }}>
      {yAxisValues.map((value, index) => (
        <YAxisLabel
          key={index}
          title={`$${Number(value).toLocaleString(undefined, {
            maximumFractionDigits: 6,
          })}`}
        >
          {formatYAxisValueSimple(value)}
        </YAxisLabel>
      ))}
    </YAxisContainer>
  );
};

CustomYAxis.propTypes = {
  data: PropTypes.array,
  height: PropTypes.number,
};
